import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, ChevronDown, Trash2, Copy, X } from "lucide-react";
import "./GoalsBoard.css";
import { API_BASE } from "../../config";
import { useAuth } from "../../auth/AuthProvider";
import ConfirmDialog from "../../components/ConfirmDialog";

/** ---------- Types ---------- */
type Task = {
  id: string;
  title: string;
  desc?: string;
  completed_at?: number;
  /** who ticked it off — "therapist" | "client" */
  completed_by?: string;
  /** who created it — used by the client-app permission model */
  created_by?: string;
  /** homework owner — "therapist" | "client" */
  assignee?: string;
  /** YYYY-MM-DD */
  due_date?: string;
};
type DragPayload =
  | { kind: "task"; taskId: string; fromLaneId: string }
  | { kind: "done"; taskId: string; fromLaneId: string };

type Lane = {
  id: string;
  title: string;
  desc?: string;
  createdAt: number;
  created_by?: string;
  tasks: Task[];
  completed: Task[];
};

type ServerBoard = {
  id: string;
  title?: string;
  description?: string;
  lanes: Lane[];
  updated_at?: number;
};

/** Per-patient cache key — a global key leaked one client's goals into
 * another client's view while their board loaded. */
const lsKeyFor = (patientId: string) => `TJ_GOALS_V12_${patientId}`;
const uid = () => crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2, 10);

const WEEK_MS = 7 * 86400_000;

class HttpError extends Error {
  status: number;
  body: any;
  constructor(status: number, message: string, body: any) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface GoalsBoardProps {
  patientId?: string;
}

const GoalsBoard: React.FC<GoalsBoardProps> = ({ patientId }) => {
  const { t } = useTranslation();
  const { ensureToken } = useAuth();

  /** ---------- authed fetch ---------- */
  const buildHeaders = async (json = false) => {
    const token = (await ensureToken()) || "";
    const h: Record<string, string> = {};
    if (token) h.Authorization = `Bearer ${token}`;
    if (json) h["Content-Type"] = "application/json";
    return h;
  };
  const request = async <T,>(method: string, path: string, body?: any): Promise<T> => {
    const r = await fetch(`${API_BASE}${path}`, {
      method,
      headers: await buildHeaders(body !== undefined),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const txt = await r.text();
    let parsed: any = null;
    try {
      parsed = txt ? JSON.parse(txt) : null;
    } catch {
      parsed = null;
    }
    if (!r.ok) {
      throw new HttpError(r.status, parsed?.error || txt || `${r.status} ${r.statusText}`, parsed);
    }
    return (parsed ?? {}) as T;
  };

  /** ---------- State + persistence ---------- */
  const [boardId, setBoardId] = useState<string | null>(null);
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [expandedCompleted, setExpandedCompleted] = useState<Set<string>>(new Set());
  const [filterText, setFilterText] = useState("");
  const [confirmDeleteLane, setConfirmDeleteLane] = useState<Lane | null>(null);
  /** tasks mid completion animation */
  const [completingIds, setCompletingIds] = useState<Set<string>>(new Set());
  /** lane currently hovered by a drag (drop-to-complete affordance) */
  const [dropTargetLane, setDropTargetLane] = useState<string | null>(null);

  /** server's updated_at — sent back as expected_updated_at so concurrent
   * edits (client app, agent, another tab) 409 instead of being overwritten */
  const updatedAtRef = useRef<number | null>(null);
  /** suppress the save effect once after we replace lanes from the server */
  const skipNextSaveRef = useRef(false);

  const toggleCompletedSection = (laneId: string) => {
    setExpandedCompleted((prev) => {
      const next = new Set(prev);
      if (next.has(laneId)) next.delete(laneId);
      else next.add(laneId);
      return next;
    });
  };

  // initial load: fetch boards (or create one)
  useEffect(() => {
    if (!patientId) {
      setBoardId(null);
      setLanes([]);
      setLoading(false);
      return;
    }

    // warm start from this patient's own cache only
    try {
      const cached = JSON.parse(localStorage.getItem(lsKeyFor(patientId)) || "null");
      if (Array.isArray(cached)) {
        skipNextSaveRef.current = true;
        setLanes(cached);
      } else {
        setLanes([]);
      }
    } catch {
      setLanes([]);
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setNotice(null);
        const qs = `?patient_id=${encodeURIComponent(patientId)}`;
        const list = await request<{ ok: boolean; items: ServerBoard[] }>("GET", `/boards${qs}`);
        if (cancelled) return;
        const items = list.items || [];
        // multiple boards: use the most recently updated one, but say so —
        // silently showing items[0] hid agent-created boards entirely
        let b = [...items].sort((a, x) => (x.updated_at || 0) - (a.updated_at || 0))[0];
        if (items.length > 1) {
          console.error(`[GoalsBoard] patient ${patientId} has ${items.length} boards; showing the most recent`);
          setNotice(t("goals.multipleBoards", { count: items.length }));
        }
        if (!b) {
          const created = await request<{ ok: boolean; board: ServerBoard }>("POST", "/boards", {
            title: t("goals.defaultBoardTitle"),
            description: t("goals.defaultBoardDesc"),
            lanes: [],
            patient_id: patientId,
          });
          b = created.board;
        }
        if (cancelled) return;
        setBoardId(b.id);
        updatedAtRef.current = b.updated_at ?? null;
        skipNextSaveRef.current = true;
        setLanes(Array.isArray(b.lanes) ? b.lanes : []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // keep per-patient local cache
  useEffect(() => {
    if (!patientId) return;
    try {
      localStorage.setItem(lsKeyFor(patientId), JSON.stringify(lanes));
    } catch (e) {
      console.error("[GoalsBoard] cache write failed:", e);
    }
  }, [lanes, patientId]);

  // debounced PUT to server with optimistic concurrency
  const saveTimer = useRef<number | null>(null);
  const pendingSaveRef = useRef<(() => Promise<void>) | null>(null);
  useEffect(() => {
    if (!boardId || loading) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    if (saveTimer.current) window.clearTimeout(saveTimer.current);

    const doSave = async () => {
      pendingSaveRef.current = null;
      setSaveState("saving");
      try {
        const res = await request<{ ok: boolean; board: ServerBoard }>(
          "PUT",
          `/boards/${encodeURIComponent(boardId)}`,
          { lanes, expected_updated_at: updatedAtRef.current }
        );
        updatedAtRef.current = res.board?.updated_at ?? null;
        setSaveState("saved");
        setError(null);
      } catch (e: any) {
        if (e instanceof HttpError && e.status === 409 && e.body?.board) {
          // someone else (client app, agent, another tab) changed the board —
          // reload their version instead of overwriting it
          const server = e.body.board as ServerBoard;
          updatedAtRef.current = server.updated_at ?? null;
          skipNextSaveRef.current = true;
          setLanes(Array.isArray(server.lanes) ? server.lanes : []);
          setNotice(t("goals.conflictReloaded"));
          setSaveState("idle");
          return;
        }
        console.error("[GoalsBoard] save failed:", e);
        setError(e?.message || String(e));
        setSaveState("error");
      }
    };

    pendingSaveRef.current = doSave;
    saveTimer.current = window.setTimeout(() => void doSave(), 600);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, boardId]);

  // Flush any pending save on unmount to prevent data loss
  useEffect(() => {
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      if (pendingSaveRef.current) void pendingSaveRef.current();
    };
  }, []);

  const retrySave = () => {
    if (pendingSaveRef.current) {
      void pendingSaveRef.current();
    } else {
      // re-trigger the save effect with current lanes
      setLanes((p) => [...p]);
    }
  };

  /** ---------- Lane ops ---------- */
  const addEmptyLane = () => {
    setLanes((p) => [
      ...p,
      {
        id: uid(),
        title: "",
        desc: "",
        createdAt: Date.now(),
        created_by: "therapist",
        tasks: [],
        completed: [],
      },
    ]);
  };
  const setLaneTitle = (laneId: string, title: string) =>
    setLanes((p) => p.map((l) => (l.id === laneId ? { ...l, title } : l)));
  const setLaneDesc = (laneId: string, desc: string) =>
    setLanes((p) => p.map((l) => (l.id === laneId ? { ...l, desc } : l)));
  const removeLane = (laneId: string) =>
    setLanes((p) => p.filter((l) => l.id !== laneId));

  /** ---------- Task ops ---------- */
  const deleteTask = (laneId: string, taskId: string) =>
    setLanes((p) =>
      p.map((l) => (l.id === laneId ? { ...l, tasks: l.tasks.filter((t) => t.id !== taskId) } : l))
    );

  const duplicateTask = (laneId: string, taskId: string) =>
    setLanes((p) =>
      p.map((l) => {
        if (l.id !== laneId) return l;
        const src = l.tasks.find((t) => t.id === taskId);
        if (!src) return l;
        const copy: Task = {
          id: uid(),
          title: src.title,
          desc: src.desc,
          assignee: src.assignee,
          due_date: src.due_date,
          created_by: "therapist",
        };
        return { ...l, tasks: [...l.tasks, copy] };
      })
    );

  const deleteCompletedTask = (laneId: string, taskId: string) =>
    setLanes((p) =>
      p.map((l) =>
        l.id === laneId ? { ...l, completed: l.completed.filter((t) => t.id !== taskId) } : l
      )
    );

  // complete a task: brief strike-through beat, then move to completed
  const completeTask = (laneId: string, taskId: string) => {
    setCompletingIds((prev) => new Set(prev).add(taskId));
    window.setTimeout(() => {
      setCompletingIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      setLanes((prev) =>
        prev.map((l) => {
          if (l.id !== laneId) return l;
          const task = l.tasks.find((t) => t.id === taskId);
          if (!task) return l;
          return {
            ...l,
            tasks: l.tasks.filter((t) => t.id !== taskId),
            completed: [
              ...l.completed,
              { ...task, completed_at: Date.now(), completed_by: "therapist" },
            ],
          };
        })
      );
    }, 380);
  };

  // uncomplete a task (move from completed back to tasks)
  const uncompleteTask = (laneId: string, taskId: string) => {
    setLanes((prev) =>
      prev.map((l) => {
        if (l.id !== laneId) return l;
        const task = l.completed.find((t) => t.id === taskId);
        if (!task) return l;
        const { completed_at: _ca, completed_by: _cb, ...taskWithout } = task;
        return {
          ...l,
          completed: l.completed.filter((t) => t.id !== taskId),
          tasks: [...l.tasks, taskWithout],
        };
      })
    );
  };

  /** ---------- Task editor drawer ---------- */
  const [editor, setEditor] = useState<{ laneId: string; taskId: string } | null>(null);
  const [edTitle, setEdTitle] = useState("");
  const [edDesc, setEdDesc] = useState("");
  const [edAssignee, setEdAssignee] = useState<string>("");
  const [edDueDate, setEdDueDate] = useState<string>("");

  const openEditorFor = (laneId: string, taskId: string) => {
    const lane = lanes.find((l) => l.id === laneId);
    const task = lane?.tasks.find((t) => t.id === taskId);
    setEditor({ laneId, taskId });
    setEdTitle(task?.title ?? "");
    setEdDesc(task?.desc ?? "");
    setEdAssignee(task?.assignee ?? "");
    setEdDueDate(task?.due_date ?? "");
  };
  const applyEditor = () => {
    if (!editor) return;
    const { laneId, taskId } = editor;
    setLanes((p) =>
      p.map((l) =>
        l.id === laneId
          ? {
              ...l,
              tasks: l.tasks.map((tk) =>
                tk.id === taskId
                  ? {
                      ...tk,
                      title: edTitle.trim(),
                      desc: edDesc.trim(),
                      assignee: edAssignee || undefined,
                      due_date: edDueDate || undefined,
                    }
                  : tk
              ),
            }
          : l
      )
    );
  };
  const saveEditor = () => {
    applyEditor();
    setEditor(null);
  };
  const closeEditor = () => {
    // Cancel/X/scrim discard pending edits — only the explicit Save button persists them.
    setEditor(null);
  };
  const removeFromEditor = () => {
    if (!editor) return;
    deleteTask(editor.laneId, editor.taskId);
    setEditor(null);
  };
  const duplicateFromEditor = () => {
    if (!editor) return;
    duplicateTask(editor.laneId, editor.taskId);
  };

  const addEmptyTaskAndOpen = (laneId: string) => {
    const id = uid();
    setLanes((p) =>
      p.map((l) =>
        l.id === laneId
          ? { ...l, tasks: [...l.tasks, { id, title: "", desc: "", created_by: "therapist" }] }
          : l
      )
    );
    setTimeout(() => openEditorFor(laneId, id), 0);
  };

  /** ---------- DnD ---------- */
  const allowDrop: React.DragEventHandler = (e) => {
    if (Array.from(e.dataTransfer.types).includes("application/x-lane-task")) e.preventDefault();
  };
  const onDragStartTask = (e: React.DragEvent, laneId: string, taskId: string) => {
    const payload: DragPayload = { kind: "task", taskId, fromLaneId: laneId };
    e.dataTransfer.setData("application/x-lane-task", JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "move";
  };

  const computeInsertX = (container: HTMLElement, clientX: number) => {
    const cards = Array.from(container.querySelectorAll<HTMLElement>("[data-card='1']"));
    if (!cards.length) return 0;
    let idx = cards.length;
    for (let i = 0; i < cards.length; i++) {
      const r = cards[i].getBoundingClientRect();
      const mid = r.left + r.width / 2;
      if (clientX < mid) {
        idx = i;
        break;
      }
    }
    return idx;
  };

  const onDropToLane = (e: React.DragEvent, toLaneId: string) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/x-lane-task");
    if (!raw) return;
    let payload: DragPayload | null = null;
    try {
      payload = JSON.parse(raw);
    } catch {}
    if (!payload) return;

    const container = (e.currentTarget as HTMLElement).querySelector(".gl-trackList") as
      | HTMLElement
      | null;
    const insertAt = container ? computeInsertX(container, e.clientX) : undefined;

    setLanes((prev) => {
      const fromLane = prev.find((l) => l.id === payload!.fromLaneId);
      const toLane = prev.find((l) => l.id === toLaneId);
      if (!fromLane || !toLane) return prev;

      const moving =
        payload!.kind === "task"
          ? fromLane.tasks.find((t) => t.id === payload!.taskId)
          : fromLane.completed.find((t) => t.id === payload!.taskId);
      if (!moving) return prev;

      return prev.map((l) => {
        if (l.id === fromLane.id) {
          if (payload!.kind === "task")
            l = { ...l, tasks: l.tasks.filter((t) => t.id !== moving.id) };
          else l = { ...l, completed: l.completed.filter((t) => t.id !== moving.id) };
        }
        if (l.id === toLane.id) {
          const next = [...l.tasks];
          if (typeof insertAt === "number") next.splice(Math.min(insertAt, next.length), 0, moving);
          else next.push(moving);
          l = { ...l, tasks: next };
        }
        return l;
      });
    });
  };

  const onDropToGoal = (e: React.DragEvent, toLaneId: string) => {
    e.preventDefault();
    setDropTargetLane(null);
    const raw = e.dataTransfer.getData("application/x-lane-task");
    if (!raw) return;
    let payload: DragPayload | null = null;
    try {
      payload = JSON.parse(raw);
    } catch {}
    if (!payload) return;

    setLanes((prev) => {
      const fromLane = prev.find((l) => l.id === payload!.fromLaneId);
      const toLane = prev.find((l) => l.id === toLaneId);
      if (!fromLane || !toLane) return prev;

      const moving =
        payload!.kind === "task"
          ? fromLane.tasks.find((t) => t.id === payload!.taskId)
          : fromLane.completed.find((t) => t.id === payload!.taskId);
      if (!moving) return prev;

      return prev.map((l) => {
        if (l.id === fromLane.id) {
          if (payload!.kind === "task")
            l = { ...l, tasks: l.tasks.filter((t) => t.id !== moving.id) };
          else l = { ...l, completed: l.completed.filter((t) => t.id !== moving.id) };
        }
        if (l.id === toLane.id) {
          l = {
            ...l,
            completed: [
              ...l.completed,
              {
                ...moving,
                completed_at: moving.completed_at || Date.now(),
                completed_by: moving.completed_by || "therapist",
              },
            ],
          };
        }
        return l;
      });
    });
  };

  /** ---------- Ordering + filter ---------- */
  const ordered = useMemo(() => [...lanes].sort((a, b) => a.createdAt - b.createdAt), [lanes]);
  const visible = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter((l) => {
      if ((l.title || "").toLowerCase().includes(q)) return true;
      if ((l.desc || "").toLowerCase().includes(q)) return true;
      return [...l.tasks, ...l.completed].some(
        (tk) =>
          (tk.title || "").toLowerCase().includes(q) ||
          (tk.desc || "").toLowerCase().includes(q)
      );
    });
  }, [ordered, filterText]);

  const todayISO = new Date().toISOString().slice(0, 10);

  const completedThisWeek = (lane: Lane) =>
    lane.completed.filter((c) => (c.completed_at || 0) >= Date.now() - WEEK_MS).length;

  const completedByLabel = (task: Task) => {
    const date = task.completed_at
      ? new Date(task.completed_at).toLocaleDateString()
      : "";
    const who =
      task.completed_by === "client"
        ? t("goals.byClient")
        : task.completed_by === "therapist"
          ? t("goals.byTherapist")
          : "";
    return [date, who].filter(Boolean).join(" · ");
  };

  /** ---------- Render ---------- */
  return (
    <div className="gl-wrap">
      <div className="gl-toolbar">
        <button
          className="gl-pillBtn"
          onClick={addEmptyLane}
          disabled={loading}
          title={t("goals.addGoal")}
        >
          <Plus size={15} /> {t("goals.addGoal")}
        </button>
        <div className="gl-toolbarRight">
          <span
            className={`gl-saveState gl-saveState--${saveState}`}
            role="status"
            aria-live="polite"
          >
            {saveState === "saving" && t("goals.saving")}
            {saveState === "saved" && t("goals.saved")}
            {saveState === "error" && (
              <>
                {t("goals.saveFailed")}{" "}
                <button className="gl-linkBtn" onClick={retrySave}>
                  {t("goals.retry")}
                </button>
              </>
            )}
          </span>
          <input
            type="text"
            className="gl-filter"
            placeholder={t("goals.filterPlaceholder")}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            aria-label={t("goals.filterPlaceholder")}
          />
        </div>
      </div>

      {loading && <div className="rc-muted">{t("goals.loadingBoard")}</div>}
      {error && (
        <div className="rc-error" style={{ marginBottom: 8 }}>
          {t("common.error")}: {error}
        </div>
      )}
      {notice && (
        <div className="gl-notice" role="status">
          {notice}
          <button className="gl-linkBtn" onClick={() => setNotice(null)} aria-label={t("common.close")}>
            ✕
          </button>
        </div>
      )}

      <div className="gl-board">
        {visible.length === 0 ? (
          <div className="rc-empty">
            {ordered.length === 0 ? t("goals.noGoals") : t("goals.noMatch")}
          </div>
        ) : (
          visible.map((lane) => {
            const total = lane.tasks.length + lane.completed.length;
            const pct = total > 0 ? Math.round((lane.completed.length / total) * 100) : 0;
            const week = completedThisWeek(lane);
            return (
              <div key={lane.id} className="gl-laneRow">
                <section className="gl-track">
                  {/* Goal header — drop a task here to complete it */}
                  <div
                    className={`gl-goalHeader ${dropTargetLane === lane.id ? "gl-goalHeader--dropTarget" : ""}`}
                    onDragOver={allowDrop}
                    onDragEnter={(e) => {
                      if (Array.from(e.dataTransfer.types).includes("application/x-lane-task")) {
                        setDropTargetLane(lane.id);
                      }
                    }}
                    onDragLeave={(e) => {
                      if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
                        setDropTargetLane(null);
                      }
                    }}
                    onDrop={(e) => onDropToGoal(e, lane.id)}
                    title={t("goals.dropToComplete")}
                  >
                    <div className="gl-goalInfo">
                      <input
                        className="gl-goalTitleInput"
                        value={lane.title}
                        placeholder={t("goals.goalTitle")}
                        onChange={(e) => setLaneTitle(lane.id, e.target.value)}
                        onBlur={(e) => setLaneTitle(lane.id, e.target.value.trim())}
                        aria-label={t("goals.goalTitle")}
                      />
                      <input
                        className="gl-goalDescInput"
                        value={lane.desc || ""}
                        placeholder={t("goals.goalDesc")}
                        onChange={(e) => setLaneDesc(lane.id, e.target.value)}
                        onBlur={(e) => setLaneDesc(lane.id, e.target.value.trim())}
                        aria-label={t("goals.goalDesc")}
                      />
                    </div>
                    <div className="gl-goalHeaderActions">
                      {week > 0 && (
                        <span className="gl-goalBadge gl-goalBadge--week">
                          {t("goals.completedThisWeek", { count: week })}
                        </span>
                      )}
                      {total > 0 && (
                        <span
                          className="gl-goalBadge gl-goalBadge--progress"
                          aria-label={t("goals.progress")}
                        >
                          {lane.completed.length}/{total}
                        </span>
                      )}
                      <button
                        className="gl-ghost gl-ghost--icon"
                        title={t("goals.addTask")}
                        aria-label={t("goals.addTask")}
                        onClick={() => addEmptyTaskAndOpen(lane.id)}
                      >
                        <Plus size={14} />
                      </button>
                      <button
                        className="gl-ghost gl-ghost--icon gl-danger"
                        title={t("goals.removeGoal")}
                        aria-label={t("goals.removeGoal")}
                        onClick={() => setConfirmDeleteLane(lane)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Progress bar */}
                  {total > 0 && (
                    <div className="gl-progress" aria-hidden>
                      <div className="gl-progressFill" style={{ width: `${pct}%` }} />
                    </div>
                  )}

                  {/* Tasks nested under goal */}
                  <div
                    className="gl-trackList"
                    role="list"
                    onDragOver={allowDrop}
                    onDrop={(e) => onDropToLane(e, lane.id)}
                  >
                    {lane.tasks.length === 0 ? (
                      <div className="gl-emptyTasks">{t("goals.noTasks")}</div>
                    ) : (
                      lane.tasks.map((task) => {
                        const overdue =
                          !!task.due_date && task.due_date < todayISO;
                        const leaving = completingIds.has(task.id);
                        return (
                          <article
                            key={task.id}
                            data-card="1"
                            className={`gl-card ${leaving ? "gl-card--leaving" : ""}`}
                            draggable
                            onDragStart={(e) => onDragStartTask(e, lane.id, task.id)}
                            role="listitem"
                            title={t("goals.clickToEdit")}
                          >
                            <label className="gl-checkbox" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={leaving}
                                onChange={() => completeTask(lane.id, task.id)}
                                aria-label={t("goals.markComplete", {
                                  title: task.title || t("goals.untitledTask"),
                                })}
                              />
                            </label>
                            <div
                              className="gl-cardText"
                              onClick={() => openEditorFor(lane.id, task.id)}
                            >
                              <div className="gl-cardTitle">
                                {task.title || t("goals.untitledTask")}
                              </div>
                              <div className="gl-cardDesc">
                                {task.desc || t("goals.addDescription")}
                              </div>
                              {(task.assignee || task.due_date) && (
                                <div className="gl-cardMeta">
                                  {task.assignee === "client" && (
                                    <span className="gl-metaChip gl-metaChip--client">
                                      {t("goals.assigneeClient")}
                                    </span>
                                  )}
                                  {task.due_date && (
                                    <span
                                      className={`gl-metaChip ${overdue ? "gl-metaChip--overdue" : ""}`}
                                    >
                                      {overdue ? t("goals.overdue") + " · " : ""}
                                      {task.due_date}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="gl-cardActions" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="rc-iconBtn"
                                title={t("common.duplicate")}
                                aria-label={t("common.duplicate")}
                                onClick={() => duplicateTask(lane.id, task.id)}
                              >
                                <Copy size={13} />
                              </button>
                              <button
                                className="rc-iconBtn rc-iconBtn--danger"
                                title={t("common.delete")}
                                aria-label={t("common.delete")}
                                onClick={() => deleteTask(lane.id, task.id)}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </article>
                        );
                      })
                    )}
                  </div>

                  {/* Collapsible Completed Section */}
                  {lane.completed.length > 0 && (
                    <div className="gl-completedSection">
                      <button
                        type="button"
                        className="gl-completedToggle"
                        onClick={() => toggleCompletedSection(lane.id)}
                        aria-expanded={expandedCompleted.has(lane.id)}
                      >
                        <ChevronDown
                          size={16}
                          style={{
                            transform: expandedCompleted.has(lane.id)
                              ? "rotate(0deg)"
                              : "rotate(-90deg)",
                            transition: "transform 0.15s ease",
                          }}
                        />
                        <span>
                          {t("goals.completed")} ({lane.completed.length})
                        </span>
                      </button>

                      {expandedCompleted.has(lane.id) && (
                        <div className="gl-completedList">
                          {lane.completed.map((task) => (
                            <article
                              key={task.id}
                              className="gl-card gl-card--completed"
                              draggable
                              onDragStart={(e) => {
                                const payload: DragPayload = {
                                  kind: "done",
                                  taskId: task.id,
                                  fromLaneId: lane.id,
                                };
                                e.dataTransfer.setData(
                                  "application/x-lane-task",
                                  JSON.stringify(payload)
                                );
                                e.dataTransfer.effectAllowed = "move";
                              }}
                            >
                              <label className="gl-checkbox" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={true}
                                  onChange={() => uncompleteTask(lane.id, task.id)}
                                  aria-label={t("goals.restoreTask", {
                                    title: task.title || t("goals.untitledTask"),
                                  })}
                                />
                              </label>
                              <div className="gl-cardText">
                                <div className="gl-cardTitle gl-cardTitle--completed">
                                  {task.title || t("goals.untitledTask")}
                                </div>
                                {task.desc && (
                                  <div className="gl-cardDesc gl-cardDesc--completed">
                                    {task.desc}
                                  </div>
                                )}
                                {completedByLabel(task) && (
                                  <div className="gl-cardMeta">
                                    <span className="gl-metaChip gl-metaChip--done">
                                      ✓ {completedByLabel(task)}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div
                                className="gl-cardActions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  className="rc-iconBtn rc-iconBtn--danger"
                                  title={t("common.delete")}
                                  aria-label={t("common.delete")}
                                  onClick={() => deleteCompletedTask(lane.id, task.id)}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </article>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </section>
              </div>
            );
          })
        )}
      </div>

      {/* Goal delete confirmation */}
      <ConfirmDialog
        open={!!confirmDeleteLane}
        title={t("goals.removeGoal")}
        message={t("goals.confirmDeleteGoal", {
          title: confirmDeleteLane?.title || t("goals.untitledTask"),
          count:
            (confirmDeleteLane?.tasks.length || 0) +
            (confirmDeleteLane?.completed.length || 0),
        })}
        confirmLabel={t("common.delete")}
        variant="danger"
        onConfirm={() => {
          if (confirmDeleteLane) removeLane(confirmDeleteLane.id);
          setConfirmDeleteLane(null);
        }}
        onCancel={() => setConfirmDeleteLane(null)}
      />

      {/* Task editor drawer */}
      {editor && (
        <>
          <div className="gl-drawerScrim" onClick={closeEditor} />
          <div className="gl-drawer" role="dialog" aria-modal="true">
            <div className="gl-drawerHead">
              <div className="gl-drawerTitle">{t("goals.editTask")}</div>
              <button
                className="gl-ghost gl-ghost--icon"
                onClick={closeEditor}
                aria-label={t("common.close")}
              >
                <X size={15} />
              </button>
            </div>

            <div>
              <div className="gl-label">{t("prompts.editor.name")}</div>
              <input
                className="gl-input"
                placeholder={t("goals.taskTitle")}
                value={edTitle}
                onChange={(e) => setEdTitle(e.target.value)}
              />
            </div>

            <div>
              <div className="gl-label">{t("prompts.editor.description")}</div>
              <textarea
                className="gl-input"
                placeholder={t("goals.taskDesc")}
                rows={6}
                value={edDesc}
                onChange={(e) => setEdDesc(e.target.value)}
              />
            </div>

            <div className="gl-drawerRow">
              <div className="gl-drawerCol">
                <div className="gl-label">{t("goals.assignee")}</div>
                <select
                  className="gl-input"
                  value={edAssignee}
                  onChange={(e) => setEdAssignee(e.target.value)}
                >
                  <option value="">{t("goals.assigneeUnset")}</option>
                  <option value="therapist">{t("goals.assigneeTherapist")}</option>
                  <option value="client">{t("goals.assigneeClient")}</option>
                </select>
              </div>
              <div className="gl-drawerCol">
                <div className="gl-label">{t("goals.dueDate")}</div>
                <input
                  className="gl-input"
                  type="date"
                  value={edDueDate}
                  onChange={(e) => setEdDueDate(e.target.value)}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="gl-ghost"
                  onClick={duplicateFromEditor}
                  title={t("goals.duplicate")}
                >
                  <Copy size={13} /> {t("goals.duplicate")}
                </button>
                <button
                  className="gl-ghost gl-danger"
                  onClick={removeFromEditor}
                  title={t("common.delete")}
                >
                  <Trash2 size={13} /> {t("common.delete")}
                </button>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="gl-btn" onClick={closeEditor}>
                  {t("common.cancel")}
                </button>
                <button className="gl-btn gl-btn--primary" onClick={saveEditor}>
                  {t("common.save")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GoalsBoard;
